import {
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  makeStyles,
} from "@material-ui/core";
import {
  formatUtcDate,
  isIsoDateString,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import React from "react";

type FieldLabels = { [key: string]: string };

type UpdatedValueProps = {
  value: any;
  fieldLabels: FieldLabels;
};

const useStyles = makeStyles((theme) => ({
  table: {
    marginTop: -17,
  },
}));

export const UpdatedValue: React.FC<UpdatedValueProps> = ({
  value,
  fieldLabels,
}) => {
  const { table } = useStyles();
  return (
    <React.Fragment>
      {renderIfEmpty(value) ||
        renderIfDate(value) ||
        renderIfPrimitive(value) ||
        renderIfPrimitiveArray(value) ||
        renderIfComplexArray(value, fieldLabels, table) ||
        renderFallback(value)}
    </React.Fragment>
  );
};

const isGuid = (value: any) =>
  typeof value === "string" &&
  value.match(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  );

const isEmpty = (value: any) =>
  value === null || value === undefined || value === "";

const isPrimitive = (value: any) => value !== Object(value);

const isPrimitiveArray = (value: any[]) =>
  Array.isArray(value) &&
  value.every((item) => isEmpty(item) || isPrimitive(item));

const isComplexArray = (value: any[]) =>
  Array.isArray(value) && value.some((value) => !isPrimitive(value));

const renderIfEmpty = (value: any) => (isEmpty(value) ? "(no value)" : null);

const renderIfDate = (value: any) =>
  isIsoDateString(value)
    ? formatUtcDate(value, standardDateFormats.DATE_TIME)
    : null;

const renderIfPrimitive = (value: any) =>
  isPrimitive(value) ? String(value) : null;

const renderIfPrimitiveArray = (value: any) =>
  isPrimitiveArray(value)
    ? (value as any[])
        .map((item) => renderIfEmpty(item) || renderIfPrimitive(item))
        .join("; ")
    : null;

const renderIfComplexArray = (
  value: any[],
  fieldLabels: FieldLabels,
  className: string
) => {
  if (!isComplexArray(value)) {
    return;
  }

  const keys = getAllKeysPresent(value);

  const headerContent = keys.map((key) => <TableCell>{key}</TableCell>);

  const bodyContent = value.map((item) => (
    <TableRow>
      {keys.map((key) => (
        <TableCell>
          <UpdatedValue value={item[key]} fieldLabels={fieldLabels} />
        </TableCell>
      ))}
    </TableRow>
  ));

  return (
    <TableContainer className={className}>
      <Table>
        <TableHead>
          <TableRow>{headerContent}</TableRow>
        </TableHead>
        <TableBody>{bodyContent}</TableBody>
      </Table>
    </TableContainer>
  );
};

const getAllKeysPresent = (arrayOfObjects: any[]) => {
  // get an array of key names for each object present (an array of string arrays)...
  const arrayOfKeys = arrayOfObjects
    .map((item) => Object.keys(item))
    // ... now sort it so that the longest set of keys is first.  This gives us
    //  the "fullest" object first and any e.g. inserts where only a few values are present later on.
    //  This gives us the best chance of returning the keys in the order they appear in a full object
    .sort((a, b) => b.length - a.length);

  // flatten these arrays, there may be lots of repetition...
  const allKeys = ([] as string[]).concat(...arrayOfKeys);

  // ... so now make the array values unique
  const uniqueKeys = allKeys.filter((item, i, ar) => ar.indexOf(item) === i);

  // do not return fields where there are guids present
  const interestingFields = uniqueKeys.filter(
    (key) => !arrayOfObjects.some((item) => isGuid(item[key]))
  );

  return interestingFields;
};

const renderFallback = (value: any) => String(value); // todo: logging if we hit here?
